/**
 * Finding one piece of wording in a library of hundreds — searching, narrowing, counting and
 * ordering, as pure functions.
 *
 * ── WHY THIS IS A FILE AND NOT A FEW COMPUTEDS ──────────────────────────────────────
 *
 * Meta caps a WhatsApp Business Account at 250 templates on an unverified business portfolio and
 * up to 6,000 on a verified one. The screen this feeds shipped with no search, no filter and no
 * paging, which is fine for the eight templates a new account has and useless at forty — and
 * "useless at forty" is not a hypothetical for a business that runs a template per branch per
 * language.
 *
 * Every rule here is about an owner looking for something, so they are all testable in
 * isolation: whether a search over Arabic body text finds the row, whether the status counts
 * agree with the rows on screen, whether the first thing she sees is the template that needs
 * her. Those are exactly the things that break silently in a computed.
 *
 * ── WHAT THE DEFAULT ORDER IS, AND WHY IT IS NOT ALPHABETICAL ───────────────────────
 *
 * Refused, paused, disabled and low-quality templates first. WhatsApp pauses a template that
 * reaches its lowest quality rating — 3 hours, then 6, then permanently disabled — and the only
 * warning before that is a rating the owner has to have SEEN. An alphabetical list buries it
 * under whatever happens to start with an 'a'. Within each group the order is by name, which is
 * stable and is what she scans by.
 */

import { needsAttention, QUALITY_SCORES, statusOf } from './template-facts.js';

/** Nothing typed, nothing narrowed. */
export const BLANK_FILTER = Object.freeze({
  search: '',
  status: '',
  category: '',
  language: '',
  quality: '',
  attention: false,
});

/** How many rows are mounted before "show more". Hundreds of rows at once is a slow screen. */
export const PAGE_SIZE = 25;

/** The value the status filter uses for a template WhatsApp has told us nothing about. */
export const UNTOLD = 'UNTOLD';

const fold = value =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

/** `GREEN` → `HIGH`, and a template with no rating at all → `UNTOLD`. */
export const qualityKey = template => {
  const score = template?.quality?.score;
  if (typeof score !== 'string' || score === '') return UNTOLD;
  return (
    QUALITY_SCORES[score.trim().toUpperCase()] ?? score.trim().toUpperCase()
  );
};

export const statusKey = template => statusOf(template) || UNTOLD;

/**
 * The search, and what it searches is the point: the NAME and the MESSAGE.
 *
 * A template's name is Meta's (`eid_offer_v2_final`) and an owner does not remember it — she
 * remembers what the message says, in her own language. Searching only names would be a search
 * box that fails on the one thing she can recall. The language code is matched too, because
 * "ar" is a perfectly reasonable thing to type when looking for the Arabic version.
 */
const searchable = template =>
  [template?.name, template?.body, template?.language].map(fold).join(' ');

export const matches = (template, filter) => {
  const search = fold(filter.search);
  if (search && !searchable(template).includes(search)) return false;
  if (filter.status && statusKey(template) !== filter.status) return false;
  if (filter.category && (template.category ?? UNTOLD) !== filter.category) {
    return false;
  }
  if (filter.language && template.language !== filter.language) return false;
  if (filter.quality && qualityKey(template) !== filter.quality) return false;
  if (filter.attention && !needsAttention(template)) return false;
  return true;
};

/**
 * Attention first, then by name. `localeCompare` with no locale argument, deliberately: a
 * template name is lowercase Latin letters, digits and underscores by Meta's own rule, so there
 * is no collation question to get wrong here.
 */
export const sortForOwner = templates =>
  [...(templates ?? [])].sort((left, right) => {
    const urgency =
      Number(needsAttention(right)) - Number(needsAttention(left));
    if (urgency !== 0) return urgency;
    return String(left.name ?? '').localeCompare(String(right.name ?? ''));
  });

export const filterTemplates = (templates, filter) =>
  sortForOwner((templates ?? []).filter(template => matches(template, filter)));

/** `{ [value]: count }` for one facet, over the WHOLE library rather than the filtered view. */
const tally = (templates, keyOf) => {
  const counts = {};
  (templates ?? []).forEach(template => {
    const key = keyOf(template);
    if (!key) return;
    counts[key] = (counts[key] ?? 0) + 1;
  });
  return counts;
};

/**
 * What the four dropdowns offer, and the counts beside them.
 *
 * COMPUTED FROM THE LIBRARY, NOT FROM A CONSTANT — so the Status list never offers "Paused" to
 * an account that has nothing paused, and never hides a status Meta has just invented. The same
 * reason `template-facts.js` keeps no closed enum: a fixed list of options is a claim about
 * Meta's vocabulary, and that claim expires.
 */
export const facets = templates => ({
  status: tally(templates, statusKey),
  category: tally(templates, template => template.category ?? UNTOLD),
  language: tally(templates, template => template.language || ''),
  quality: tally(templates, qualityKey),
  attention: (templates ?? []).filter(needsAttention).length,
  total: (templates ?? []).length,
});

export const isNarrowed = filter =>
  Boolean(
    fold(filter.search) ||
      filter.status ||
      filter.category ||
      filter.language ||
      filter.quality ||
      filter.attention
  );
